import { Injectable } from '@angular/core';
import { environment } from '@environments/environment';

export type EncryptedEnvelope = {
  alg: 'RSA-OAEP-256/AES-256-GCM';
  ek: string;
  iv: string;
  ct: string;
  aad?: string;
  payload_encrypt: 'true';
};

@Injectable({ providedIn: 'root' })
export class PayloadEncryptionService {
  // Public key PEM (SPKI)
  private readonly PUBLIC_KEY_PEM = environment.pbKey as string;

  private rsaPublicKeyPromise?: Promise<CryptoKey>;

  async encryptJson(payload: unknown, aadObject?: unknown): Promise<EncryptedEnvelope> {
    const plaintext = new TextEncoder().encode(JSON.stringify(payload));

    const aadBytes =
      aadObject !== undefined ? new TextEncoder().encode(JSON.stringify(aadObject)) : undefined;

    // 1️⃣ Generate random AES-256 key (32 bytes)
    const aesKeyRaw = crypto.getRandomValues(new Uint8Array(32));

    const aesKey = await crypto.subtle.importKey('raw', aesKeyRaw, { name: 'AES-GCM' }, false, [
      'encrypt'
    ]);

    // 2️⃣ AES-GCM encrypt
    const iv = crypto.getRandomValues(new Uint8Array(12)); // 12-byte IV recommended

    const gcmParams: AesGcmParams = {
      name: 'AES-GCM',
      iv,
      additionalData: aadBytes,
      tagLength: 128 // 16-byte tag
    };

    const ciphertextBuf = await crypto.subtle.encrypt(gcmParams, aesKey, plaintext);

    // 3️⃣ RSA-OAEP(SHA-256) encrypt the AES key
    const rsaKey = await this.getRsaPublicKey();

    const wrappedKeyBuf = await crypto.subtle.encrypt({ name: 'RSA-OAEP' }, rsaKey, aesKeyRaw);

    return {
      alg: 'RSA-OAEP-256/AES-256-GCM',
      ek: toBase64(new Uint8Array(wrappedKeyBuf)),
      iv: toBase64(iv),
      ct: toBase64(new Uint8Array(ciphertextBuf)),
      aad: aadBytes ? toBase64(aadBytes) : undefined,
      payload_encrypt: 'true'
    };
  }

  private getRsaPublicKey(): Promise<CryptoKey> {
    if (!this.rsaPublicKeyPromise) {
      this.rsaPublicKeyPromise = importRsaOaepPublicKeyFromPem(this.PUBLIC_KEY_PEM);
    }
    return this.rsaPublicKeyPromise;
  }
}

/** PEM (SPKI) -> CryptoKey for RSA-OAEP + SHA-256 */
async function importRsaOaepPublicKeyFromPem(pem: string): Promise<CryptoKey> {
  const spkiDer = pemToArrayBuffer(pem);

  return crypto.subtle.importKey(
    'spki',
    spkiDer,
    {
      name: 'RSA-OAEP',
      hash: 'SHA-256'
    },
    false,
    ['encrypt']
  );
}

function pemToArrayBuffer(pem: string): ArrayBuffer {
  const b64 = pem
    .replace(/-----BEGIN PUBLIC KEY-----/g, '')
    .replace(/-----END PUBLIC KEY-----/g, '')
    .replace(/\s+/g, '');

  const bytes = Uint8Array.from(atob(b64), c => c.charCodeAt(0));
  return bytes.buffer;
}

function toBase64(bytes: Uint8Array): string {
  let binary = '';
  const chunkSize = 0x8000;

  for (let i = 0; i < bytes.length; i += chunkSize) {
    binary += String.fromCharCode(...bytes.subarray(i, i + chunkSize));
  }

  return btoa(binary);
}
